'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import GoogleLoginButton from './(auth)/components/GoogleLoginButton';
import MicrosoftLoginButton from './(auth)/components/MicrosoftLoginButton';
import data from './data/en.json';
import TextModal from './common/components/modals/general/TextModal';
import { AssistivLogoSmallSVG, AssistivLogoSVG, AssistivRobot } from './assets/branding/assistiv';

export default function HomePage(): React.JSX.Element {
  const router = useRouter();
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [modalData, setModalData] = useState({ title: '', content: '' });

  useEffect(() => {
    if (loggedIn) {
      router.replace('/student');
    }
  }, [loggedIn, router]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePos({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getGradientStyles = (isDemo: boolean) => {
    const { x, y } = mousePos;
    return {
      background: `radial-gradient(
        at ${40 + (x / window.innerWidth) * 30}% 
        ${60 + (y / window.innerHeight) * 30}%, 
        ${isDemo ? `#00c77bff 5%,` : `rgb(239,79,45) 5%,`} 
        rgb(24,49,161) 81%, 
        rgb(1,36,151) 100%
      )`,
      transition: 'background-position 0.1s ease-out',
    };
  };

  const getWarpingStyles = () => {
    const { x, y } = mousePos;
    return {
      background: `radial-gradient(
        at ${(x / window.innerWidth) * 20}% 
        ${(y / window.innerHeight) * 10}%, 
        rgba(103,155,252,0) 63%, 
        rgba(39,86,240,0.6) 70%, 
        rgb(1,36,151) 100%
      )`,
      filter: `hue-rotate(${(x / window.innerWidth) * 20 - 10}deg) brightness(1.1)`,
      transition: 'filter 0.2s ease-out, background-position 0.1s ease-out',
    };
  };

  const openModal = (isTOS: boolean): void => {
    setModalData(isTOS ? data.termsOfUse : data.privacyPolicy);
    dispatch(setModalVisible(true));
    dispatch(setModalName('TermsPolicy'));
  };

  const welcomeItems = [
    {
      title: 'AI Course Generator',
      description: 'Transform documents, policies, or transcripts into complete courses instantly.',
    },
    {
      title: 'Smart Flashcards',
      description: 'AI extracts key concepts and creates study materials automatically.',
    },
    {
      title: 'Adaptive Quizzes',
      description: 'Generate assessments that ensure true comprehension, not just completion.',
    },
  ];

  const renderFeatureIcon = (index: number): React.ReactNode => {
    if (index === 0) {
      // AI Course Generator icon
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'
        >
          <path
            d='M7.77075 0.293945C7.92576 -0.065223 8.23743 -0.112382 8.42017 0.248047C9.01247 1.812 9.32388 3.61331 9.93384 5.15332C10.0296 5.39522 10.1784 5.70381 10.3635 5.8877L10.3606 5.88867C10.5017 6.02981 10.808 6.21162 10.9983 6.29102C12.4576 6.89719 14.4278 7.10359 15.8645 7.72363C16.3608 7.9379 16.2911 8.32772 15.8186 8.52051C14.3606 9.11653 12.4553 9.3398 10.9973 9.93457C10.5096 10.1337 10.1511 10.49 9.95825 10.9727C9.33447 12.534 9.0246 14.3878 8.41724 15.9756C8.29877 16.3322 7.92248 16.2932 7.78638 15.9756C7.18024 14.4016 6.88582 12.5088 6.24438 10.9727C6.03141 10.4636 5.72516 10.1443 5.20728 9.93262C3.74292 9.33538 1.77538 9.12096 0.340088 8.50098C-0.0487884 8.33332 -0.140573 7.97379 0.25708 7.76465C1.78563 7.20389 3.49835 6.90472 5.01685 6.35156C5.60155 6.1386 5.97769 5.89036 6.22974 5.29688C6.87618 3.77464 7.12186 1.7961 7.77075 0.293945ZM8.25903 4.23633C8.16882 4.05925 8.01519 4.08205 7.93872 4.25879C7.61834 4.99929 7.49712 5.97515 7.17798 6.72559C7.05356 7.01799 6.86791 7.14016 6.57935 7.24512C5.82979 7.51777 4.98425 7.665 4.22974 7.94141C4.03348 8.04445 4.07899 8.22199 4.27075 8.30469C4.97933 8.61033 5.95119 8.71628 6.67407 9.01074C6.92959 9.11511 7.08069 9.27255 7.18579 9.52344C7.50237 10.2806 7.6474 11.2134 7.94653 11.9893C8.01372 12.1458 8.19958 12.1651 8.25806 11.9893C8.55782 11.2066 8.71093 10.293 9.0188 9.52344C9.11395 9.28558 9.29084 9.10989 9.53149 9.01172C10.2511 8.71855 11.1917 8.60818 11.9114 8.31445C12.1447 8.21941 12.1789 8.02748 11.9338 7.92188C11.2248 7.61637 10.2527 7.5145 9.53247 7.21582C9.43853 7.17668 9.28672 7.08716 9.21704 7.01758L9.21899 7.0166C9.12755 6.9259 9.05338 6.77357 9.0061 6.6543C8.70511 5.89525 8.55137 5.00714 8.25903 4.23633Z'
            fill='#F5F5F5'
          />
          <path
            d='M14.8856 11.1462C14.9632 10.9671 15.1185 10.9439 15.2098 11.1238C15.5059 11.9056 15.6618 12.806 15.9667 13.5759C16.0146 13.6969 16.0889 13.8521 16.1815 13.9441H16.1805C16.251 14.0146 16.4037 14.1055 16.4989 14.1453C17.2286 14.4484 18.2142 14.552 18.9325 14.8621C19.1803 14.9692 19.1452 15.1641 18.9091 15.2605C18.1801 15.5584 17.2278 15.6702 16.4989 15.9675C16.2551 16.0671 16.0758 16.2448 15.9794 16.4861C15.6675 17.2668 15.5126 18.1941 15.2089 18.988C15.1496 19.1661 14.9616 19.1465 14.8934 18.988C14.5903 18.201 14.4427 17.2542 14.1219 16.4861C14.0155 16.2317 13.8623 16.0724 13.6034 15.9666C12.8712 15.668 11.8874 15.5607 11.1698 15.2507C10.9754 15.1669 10.9298 14.9861 11.1288 14.8816C11.8931 14.6012 12.7494 14.4521 13.5087 14.1755C13.801 14.0691 13.9891 13.945 14.1151 13.6482C14.4383 12.887 14.5611 11.8973 14.8856 11.1462ZM15.0907 14.0876C15.0681 14.0434 15.0297 14.0493 15.0106 14.0935C14.9306 14.2786 14.8999 14.5222 14.8202 14.7097C14.7891 14.7829 14.7429 14.8134 14.6708 14.8396C14.4834 14.9077 14.2725 14.9453 14.0839 15.0144C14.0347 15.0402 14.0456 15.0846 14.0936 15.1052C14.2707 15.1816 14.5135 15.2075 14.6942 15.281C14.758 15.307 14.7959 15.3465 14.8221 15.4089C14.9013 15.5983 14.9378 15.8321 15.0126 16.0261C15.0294 16.0651 15.0761 16.0699 15.0907 16.0261C15.1657 15.8304 15.2041 15.6014 15.2811 15.4089C15.305 15.3497 15.349 15.3055 15.4091 15.281C15.5889 15.2078 15.824 15.1806 16.0038 15.1072C16.0621 15.0834 16.0709 15.0349 16.0096 15.0085C15.8324 14.9322 15.5891 14.9074 15.4091 14.8328C15.3857 14.823 15.3485 14.8003 15.3309 14.783C15.3081 14.7603 15.289 14.722 15.2772 14.6921C15.202 14.5024 15.1638 14.2803 15.0907 14.0876Z'
            fill='#F5F5F5'
          />
        </svg>
      );
    }

    if (index === 1) {
      // Smart Flashcards icon (provided SVG, scaled into 20x20 slot)
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='20'
          viewBox='0 0 20 27'
          fill='none'
        >
          <rect
            x='2.88464'
            y='3.65137'
            width='16.5517'
            height='22.069'
            rx='2.75862'
            stroke='#F5F5F5'
          />
          <rect
            x='1.18958'
            y='1.95581'
            width='15.5517'
            height='21.069'
            rx='2.25862'
            fill='#1B8097'
            stroke='#F5F5F5'
          />
          <g filter='url(#flashcards_shadow)'>
            <path
              d='M4.32749 15.2146H3.42012L6.04453 8.06726H6.93795L9.56236 15.2146H8.65498L6.51916 9.19799H6.46332L4.32749 15.2146ZM4.66253 12.4227H8.31995V13.1905H4.66253V12.4227ZM12.1414 15.3402C11.8017 15.3402 11.4934 15.2763 11.2166 15.1483C10.9397 15.018 10.7198 14.8307 10.557 14.5864C10.3941 14.3398 10.3127 14.042 10.3127 13.693C10.3127 13.3859 10.3732 13.1369 10.4942 12.9462C10.6151 12.753 10.7768 12.6018 10.9793 12.4925C11.1817 12.3831 11.405 12.3017 11.6493 12.2482C11.8959 12.1923 12.1437 12.1481 12.3927 12.1156C12.7184 12.0737 12.9825 12.0423 13.1849 12.0213C13.3896 11.9981 13.5385 11.9597 13.6316 11.9062C13.727 11.8527 13.7747 11.7596 13.7747 11.627V11.5991C13.7747 11.2547 13.6805 10.9872 13.492 10.7964C13.3059 10.6056 13.0232 10.5102 12.6439 10.5102C12.2508 10.5102 11.9425 10.5963 11.7191 10.7685C11.4958 10.9406 11.3387 11.1244 11.248 11.3199L10.4662 11.0407C10.6058 10.7149 10.792 10.4613 11.0246 10.2799C11.2596 10.0961 11.5155 9.9681 11.7924 9.89598C12.0716 9.82153 12.3461 9.7843 12.616 9.7843C12.7882 9.7843 12.986 9.80524 13.2093 9.84712C13.435 9.88667 13.6525 9.96926 13.8619 10.0949C14.0736 10.2205 14.2493 10.4102 14.3889 10.6638C14.5285 10.9174 14.5983 11.257 14.5983 11.6828V15.2146H13.7747V14.4887H13.7328C13.677 14.605 13.5839 14.7295 13.4536 14.8621C13.3233 14.9947 13.15 15.1076 12.9336 15.2006C12.7172 15.2937 12.4532 15.3402 12.1414 15.3402ZM12.267 14.6004C12.5928 14.6004 12.8673 14.5364 13.0907 14.4084C13.3163 14.2805 13.4862 14.1153 13.6002 13.9129C13.7165 13.7104 13.7747 13.4976 13.7747 13.2742V12.5204C13.7398 12.5623 13.663 12.6007 13.5443 12.6356C13.428 12.6681 13.2931 12.6972 13.1395 12.7228C12.9883 12.7461 12.8405 12.767 12.6963 12.7856C12.5544 12.8019 12.4392 12.8159 12.3508 12.8275C12.1367 12.8554 11.9367 12.9008 11.7505 12.9636C11.5667 13.0241 11.4178 13.116 11.3038 13.2393C11.1921 13.3603 11.1363 13.5255 11.1363 13.7349C11.1363 14.0211 11.2422 14.2374 11.4539 14.384C11.6679 14.5283 11.939 14.6004 12.267 14.6004Z'
              fill='#F5F5F5'
            />
          </g>
          <defs>
            <filter
              id='flashcards_shadow'
              x='3.42017'
              y='8.06738'
              width='11.6149'
              height='7.70973'
              filterUnits='userSpaceOnUse'
              colorInterpolationFilters='sRGB'
            >
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feColorMatrix
                in='SourceAlpha'
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                result='hardAlpha'
              />
              <feOffset dx='0.436782' dy='0.436782' />
              <feComposite in2='hardAlpha' operator='out' />
              <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0' />
              <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow' />
              <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape' />
            </filter>
          </defs>
        </svg>
      );
    }

    if (index === 2) {
      // Adaptive Quizzes icon
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'
        >
          <path
            d='M7.5 9.16667L10 11.6667L18.3333 3.33333M17.5 10V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H13.3333'
            stroke='#FAFAFA'
            strokeWidth='1.66667'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      );
    }

    // Default simple white dot for Smart Flashcards
    return <span className='inline-block h-[6px] w-[6px] rounded-full bg-white' aria-hidden />;
  };

  return (
    <div className='absolute left-0 top-0 w-full h-[100vh] flex flex-col md:flex-row justify-center items-center overflow-hidden bg-slate-100'>
      {/* Left panel - hidden on mobile only */}
      <div
        className='hidden md:flex md:w-[40vw] lg:w-[36vw] h-full flex-col text-white p-6 lg:p-8 space-y-3'
        style={{
          background:
            'linear-gradient(137deg, rgba(0, 88, 188, 0.90) 0%, rgba(0, 160, 53, 0.90) 128.02%)',
        }}
      >
        <div className='w-full flex flex-col mb-3 text-white items-center'>
          <AssistivLogoSVG alt='Logo' className='w-full max-w-[220px]' />
        </div>

        <div className='w-full flex justify-center mb-3 lg:mb-5'>
          <img
            src={typeof AssistivRobot === 'string' ? AssistivRobot : AssistivRobot.src}
            alt='Assistiv robot teaching with a whiteboard'
            className='w-full max-w-[230px] h-auto drop-shadow-xl'
          />
        </div>

        <div className='w-full flex flex-col mb-3 lg:mb-4'>
          <h3 className='font-bold text-[22px] lg:text-[26px] leading-snug text-white'>
            Transform Your Workforce with AI-Powered Learning
          </h3>
        </div>

        <div className='w-full flex flex-col space-y-4 lg:space-y-5 mt-1'>
          {welcomeItems.map(({ title, description }, i) => (
            <div key={i} className='w-full flex flex-col space-y-1'>
              <h4
                className='text-base lg:text-lg font-semibold flex items-center gap-2 text-white'
                style={{ color: '#FFFFFF' }}
              >
                <span className='flex items-center justify-center w-5 h-5'>
                  {renderFeatureIcon(i)}
                </span>
                <span style={{ color: '#FFFFFF' }}>{title}</span>
              </h4>
              <p
                className='text-sm lg:text-base text-white leading-relaxed pl-6 lg:pl-7'
                style={{ color: '#FFFFFF' }}
              >
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className='w-full md:w-[60vw] lg:w-[64vw] h-full flex flex-col justify-center items-center px-4 md:px-0 bg-slate-50'>
        <div className='flex flex-col space-y-6 md:space-y-8 items-center mt-0 md:mt-[-3rem] p-8 md:px-10 md:py-10 rounded-[28px] md:rounded-[32px] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.15)] border border-slate-100 w-full max-w-[420px]'>
          {/* Logo - mobile only */}
          <div className='flex md:hidden w-12 mx-auto h-fit'>
            <AssistivLogoSmallSVG alt='Logo' className='w-full' />
          </div>

          <div className='w-full space-y-1 md:space-y-2 text-center'>
            <h1 className='text-2xl md:text-[28px] font-bold leading-tight text-center'>
              Welcome back!
            </h1>
            <p className='text-sm md:text-base text-slate-600 text-center'>
              Sign in to continue your learning path
            </p>
          </div>

          <div className='flex flex-col space-y-2 w-full self-center items-stretch'>
            <div className='w-full flex justify-center'>
              <GoogleLoginButton isAdmin={true} />
            </div>
            <div className='w-full flex justify-center'>
              <MicrosoftLoginButton isAdmin={true} />
            </div>
          </div>
          <div className='w-full space-y-4 pt-2'>
            <p className='text-xs md:text-sm text-slate-600 text-center'>
              <span className='font-semibold block'>New to Assistiv?</span>
              <span>Create a Google/Microsoft account by clicking on the above options.</span>
            </p>

            <div
              className='w-full px-4 py-3 text-xs md:text-sm flex items-start gap-3'
              style={{
                borderRadius: 16,
                border: '1px solid #B2DDFF',
                background: '#EFF8FF',
                color: '#1570EF',
              }}
            >
              <div className='mt-[2px] shrink-0'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M18.3334 4.99992C18.3334 4.08325 17.5834 3.33325 16.6667 3.33325H3.33341C2.41675 3.33325 1.66675 4.08325 1.66675 4.99992M18.3334 4.99992V14.9999C18.3334 15.9166 17.5834 16.6666 16.6667 16.6666H3.33341C2.41675 16.6666 1.66675 15.9166 1.66675 14.9999V4.99992M18.3334 4.99992L10.0001 10.8333L1.66675 4.99992'
                    stroke='#1570EF'
                    strokeWidth='1.66667'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='space-y-1'>
                <p className='font-semibold' style={{ color: '#1570EF' }}>
                  Email sign-up will be available soon
                </p>
                <p style={{ color: '#1570EF' }}>
                  For now, please use your organization&apos;s SSO provider to sign in.
                </p>
              </div>
            </div>

            <p className='text-[11px] md:text-xs text-slate-500 text-center leading-relaxed'>
              By continuing, you agree to our{' '}
              <span
                className='underline cursor-pointer'
                style={{ color: '#1570EF' }}
                onClick={() => openModal(true)}
              >
                Terms of Service
              </span>{' '}
              and{' '}
              <span
                className='underline cursor-pointer'
                style={{ color: '#1570EF' }}
                onClick={() => openModal(false)}
              >
                Privacy Policy
              </span>
              .
            </p>
          </div>
        </div>
        <div className='mt-4 text-[11px] md:text-xs text-slate-600 text-center'>
          Need help? Contact your IT administrator or{' '}
          <a
            href='mailto:support@ftfplatforms.com'
            className='underline'
            style={{ color: '#1570EF' }}
          >
            FTF Support
          </a>
          .
        </div>
      </div>
      <TextModal modalName='TermsPolicy' title={modalData.title} displayText={modalData.content} />
    </div>
  );
}
